import { useState } from "react";
import { NavLink, Outlet, useNavigate } from "react-router-dom";
import { IdleWarning } from "@/components/IdleWarning";
import { SidebarClock } from "@/components/SidebarIdentity";
import { AppFooter } from "@/components/AppFooter";
import { NotificationsBell } from "@/components/NotificationsBell";
import { OverdueGate } from "@/components/OverdueGate";
import {
  SquaresFour,
  UsersThree,
  FunnelSimple,
  PhoneCall,
  Handshake,
  ChartBar,
  GearSix,
  SignOut,
  Lock,
  UserCircle,
  List as ListIcon,
  CaretDown,
  CurrencyCircleDollar,
} from "@phosphor-icons/react";
import { useAuth } from "@/context/AuthContext";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";

const NAV = [
  { to: "/dashboard", label: "Dashboard", icon: SquaresFour, testid: "nav-dashboard" },
  { to: "/prospects", label: "Prospects", icon: UsersThree, testid: "nav-prospects" },
  { to: "/opportunities", label: "Opportunities", icon: FunnelSimple, testid: "nav-opportunities" },
  { to: "/activities", label: "Activities", icon: PhoneCall, testid: "nav-activities" },
  { to: "/finance", label: "Finance", icon: CurrencyCircleDollar, testid: "nav-finance" },
  { to: "/clients", label: "Clients", icon: Handshake, testid: "nav-clients" },
  { to: "/reports", label: "Reports", icon: ChartBar, testid: "nav-reports" },
  { to: "/administration", label: "Administration", icon: GearSix, testid: "nav-administration", adminOnly: true },
];

function initials(name = "") {
  return name
    .split(" ")
    .map((n) => n[0])
    .filter(Boolean)
    .slice(0, 2)
    .join("")
    .toUpperCase();
}

export default function AppShell() {
  const { user, isAdmin, logout } = useAuth();
  const navigate = useNavigate();
  const [mobileOpen, setMobileOpen] = useState(false);

  const items = NAV.filter((n) => !n.adminOnly || isAdmin);

  const handleLogout = async () => {
    await logout();
    navigate("/login");
  };

  const SidebarContent = (
    <div className="flex h-full flex-col bg-[#002248] text-white">
      {/* Official REACH logo asset — used exactly as supplied. */}
      <div className="flex justify-center border-b border-white/10 px-6 py-5">
        <img src="/reach_logo.png" alt="reach — connect. advance. grow."
          className="h-28 w-28 rounded-2xl object-contain" data-testid="sidebar-logo" />
      </div>

      <nav className="flex-1 space-y-1 overflow-y-auto px-3 py-5">
        {items.map(({ to, label, icon: Icon, testid }) => (
          <NavLink
            key={to}
            to={to}
            data-testid={testid}
            onClick={() => setMobileOpen(false)}
            className={({ isActive }) =>
              [
                "flex items-center gap-3 rounded-md border-l-[3px] px-3 py-2.5 text-sm font-medium transition-colors",
                isActive
                  ? "border-[#65AB15] bg-white/10 text-white"
                  : "border-transparent text-white/70 hover:bg-white/5 hover:text-white",
              ].join(" ")
            }
          >
            <Icon size={19} weight="regular" />
            {label}
          </NavLink>
        ))}
      </nav>

      <SidebarClock />
    </div>
  );

  return (
    <div className="flex h-screen w-full overflow-hidden bg-white">
      {/* Desktop sidebar */}
      <aside className="hidden w-64 shrink-0 lg:block" data-testid="sidebar-desktop">
        {SidebarContent}
      </aside>

      {/* Mobile drawer */}
      {mobileOpen && (
        <div className="fixed inset-0 z-40 lg:hidden" data-testid="sidebar-mobile">
          <div className="absolute inset-0 bg-black/50" onClick={() => setMobileOpen(false)} />
          <div className="absolute left-0 top-0 h-full w-64">{SidebarContent}</div>
        </div>
      )}

      <div className="flex min-w-0 flex-1 flex-col">
        <header className="flex h-16 shrink-0 items-center justify-between border-b border-[#E4E4E7] bg-white px-4 sm:px-6">
          <div className="flex items-center gap-3">
            <button
              className="rounded-md p-2 text-[#002248] hover:bg-[#F4F4F5] lg:hidden"
              onClick={() => setMobileOpen(true)}
              data-testid="mobile-menu-button"
              aria-label="Open navigation"
            >
              <ListIcon size={22} />
            </button>
            <span className="hidden text-xs font-bold uppercase tracking-[0.2em] text-[#6D6D70] sm:inline">
              {isAdmin ? "Administrator Workspace" : "Salesperson Workspace"}
            </span>
          </div>

          <div className="flex items-center gap-2">
            <NotificationsBell />
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button
                  className="flex items-center gap-2 rounded-md border border-[#E4E4E7] py-1.5 pl-1.5 pr-2.5 transition-colors hover:bg-[#F4F4F5]"
                  data-testid="user-menu-trigger"
                >
                  <span className="relative">
                    <span className="flex h-7 w-7 items-center justify-center overflow-hidden rounded-full bg-[#002248] text-xs font-bold text-white">
                      {user?.profile_picture
                        ? <img src={user.profile_picture} alt="" className="h-full w-full object-cover" />
                        : initials(user?.preferred_name || user?.name)}
                    </span>
                    {/* Live presence dot — the session heartbeat keeps it green while signed in. */}
                    <span className="absolute -bottom-0.5 -right-0.5 h-2.5 w-2.5 rounded-full border-2 border-white bg-[#22C55E]"
                      title="Online — active session" data-testid="header-presence-dot" />
                  </span>
                  <span className="hidden text-sm font-medium text-[#09090B] sm:inline">{user?.preferred_name || user?.name}</span>
                  <CaretDown size={14} className="text-[#6D6D70]" />
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-56 rounded-md">
                <DropdownMenuLabel className="flex flex-col">
                  <span className="text-sm font-semibold text-[#09090B]">{user?.name}</span>
                  <span className="text-xs font-normal text-[#6D6D70]">{user?.email}</span>
                  <span className="mt-1 w-fit rounded-sm bg-[#65AB15]/10 px-1.5 py-0.5 text-[10px] font-bold uppercase tracking-wide text-[#4c840f]">
                    {user?.role}
                  </span>
                </DropdownMenuLabel>
                <DropdownMenuSeparator />
                <DropdownMenuItem
                  data-testid="menu-my-profile"
                  onClick={() => navigate("/profile")}
                  className="cursor-pointer"
                >
                  <UserCircle size={16} className="mr-2" /> My profile
                </DropdownMenuItem>
                <DropdownMenuItem
                  data-testid="menu-change-password"
                  onClick={() => navigate("/change-password")}
                  className="cursor-pointer"
                >
                  <Lock size={16} className="mr-2" /> Change password
                </DropdownMenuItem>
                <DropdownMenuItem
                  data-testid="menu-logout"
                  onClick={handleLogout}
                  className="cursor-pointer text-[#EF4444] focus:text-[#EF4444]"
                >
                  <SignOut size={16} className="mr-2" /> Log out
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </header>

        <main className="flex min-h-0 flex-1 flex-col overflow-y-auto bg-[#F4F6F9]">
          <IdleWarning />
          <OverdueGate />
          <div className="flex-1">
            <Outlet />
          </div>
          <AppFooter />
        </main>
      </div>
    </div>
  );
}
